/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import cx from "classnames";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import PropTypes from "prop-types";

import TimeSlotGroup from "./TimeSlotGroup";
import { getSlotMetrics } from "./utils/TimeSlots";

/**
 * Since the TimeGutter only displays the 'times' of slots in a day, and is separate
 * from the Day Columns themselves, we check to see if the range contains an offset difference
 * and, if so, change the beginning and end 'date' by a day to properly display the slots times
 * used.
 */
function adjustForDST({ min, max, localizer }) {
	if (localizer.getTimezoneOffset(min) !== localizer.getTimezoneOffset(max)) {
		return {
			start: localizer.add(min, -1, "day"),
			end: localizer.add(max, -1, "day"),
		};
	}
	return { start: min, end: max };
}

const TimeGutter = ({
	min,
	max,
	timeslots,
	step,
	localizer,
	getNow,
	resource,
	components,
	getters,
	gutterRef,
	timezone,
}) => {
	const { timeGutterWrapper: TimeGutterWrapper } = components;
	const { start, end } = useMemo(
		() => adjustForDST({ min, max, localizer }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[min?.toISOString(), max?.toISOString(), localizer],
	);
	const [slotMetrics, setSlotMetrics] = useState(
		getSlotMetrics({
			min: start,
			max: end,
			timeslots,
			step,
			localizer,
		}),
	);

	useEffect(() => {
		if (slotMetrics) {
			setSlotMetrics(
				slotMetrics.update({
					min: start,
					max: end,
					timeslots,
					step,
					localizer,
				}),
			);
		}
		/**
		 * We don't want this to fire when slotMetrics is updated as it would recursively bomb
		 */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [start?.toISOString(), end?.toISOString(), timeslots, step]);

	const renderSlot = useCallback(
		(value, idx) => {
			if (idx) return null; // don't return the first (0) idx

			const isNow = slotMetrics.dateIsInGroup(getNow(), idx);

			let zonedTime = !timezone ? value : toZonedTime(fromZonedTime(value, timezone.source), timezone.target);

			// Display the hours in either single or double digit, and minutes if they are not :00
			const intlOptions = { hour: "numeric", minute: zonedTime.getMinutes() != 0 ? "2-digit" : undefined };
			const label = new Intl.DateTimeFormat("en-US", intlOptions).format(zonedTime).toLocaleLowerCase();
			return <span className={cx("fbc-label", { "fbc-now": isNow })}>{label}</span>;
		},
		[slotMetrics, getNow, timezone],
	);

	return (
		<TimeGutterWrapper slotMetrics={slotMetrics}>
			<div className="fbc-time-gutter fbc-time-column" ref={gutterRef}>
				{slotMetrics.groups.map((grp, idx) => {
					return (
						<TimeSlotGroup
							key={idx}
							group={grp}
							resource={resource}
							components={components}
							renderSlot={renderSlot}
							getters={getters}
						/>
					);
				})}
			</div>
		</TimeGutterWrapper>
	);
};

TimeGutter.propTypes = {
	min: PropTypes.instanceOf(Date).isRequired,
	max: PropTypes.instanceOf(Date).isRequired,
	timeslots: PropTypes.number.isRequired,
	step: PropTypes.number.isRequired,
	getNow: PropTypes.func.isRequired,
	components: PropTypes.object.isRequired,
	getters: PropTypes.object,

	localizer: PropTypes.object.isRequired,
	resource: PropTypes.string,
	gutterRef: PropTypes.any,
	timezone: PropTypes.object,
};

export default React.forwardRef((props, ref) => <TimeGutter gutterRef={ref} {...props} />);
